/**
 * The board: a column of cards that accumulates as the trip takes shape.
 *
 * Under A2UI this reads the TREE — `region:main`'s `children`, in the order the
 * agent put them — rather than filtering a flat list by a `region` field. The
 * order is the agent's and is never re-sorted here.
 *
 * `region` is a HINT the layout answers: wide screens give `side` its own
 * narrower column, a phone folds everything into one, `header` is pinned above
 * both panes by `App`, and `footer` is drawn in the CHAT strip by `Chips`.
 *
 * No pan/zoom in v1 — everything is inside `#board-canvas`, which is the hook a
 * later `transform` would attach to (§6.3).
 */

import { useMemo } from 'react';
import { useSurface, type SurfaceNode } from '../a2ui/surfaceStore';
import BoardItem from './BoardItem';
import BoardEmpty from './BoardEmpty';
import ErrorBoundary from '../app/ErrorBoundary';

const MAX_STAGGER = 5;

/**
 * The cascade's index (§5.5.4): position among the cards that arrived in the
 * SAME batch — those share one `createdAt`, stamped once per `upsert()` call
 * in `surfaceStore.ts` — capped at `MAX_STAGGER`. A card from an older batch
 * that is still `phase: 'enter'` (never touched again since) gets no index at
 * all: it already played its entrance and mounting a new `--stagger-i` value
 * on it does nothing, since a CSS animation only replays when the element
 * itself (re)mounts.
 */
function staggerIndices(nodes: SurfaceNode[]): Map<string, number> {
  const entering = nodes.filter((n) => n.phase === 'enter');
  if (entering.length === 0) return new Map();
  const latest = Math.max(...entering.map((n) => n.createdAt));
  const indices = new Map<string, number>();
  let i = 0;
  for (const node of entering) {
    if (node.createdAt !== latest) continue;
    indices.set(node.id, Math.min(i, MAX_STAGGER));
    i += 1;
  }
  return indices;
}

export default function Board() {
  // Subscribe to the two maps the tree is made of — zustand compares by
  // reference, so a selector returning a fresh array would re-render forever —
  // and derive the regions from them.
  const components = useSurface((state) => state.components);
  const exiting = useSurface((state) => state.exiting);
  const focused = useSurface((state) => state.focusedId());

  const { main, side, mainStagger, sideStagger } = useMemo(() => {
    const state = useSurface.getState();
    const main = state.regionNodes('main');
    const side = state.regionNodes('side');
    return { main, side, mainStagger: staggerIndices(main), sideStagger: staggerIndices(side) };
  }, [components, exiting]);

  if (main.length === 0 && side.length === 0) return <BoardEmpty />;

  return (
    <ErrorBoundary
      label="board"
      fallback={
        <div className="board-empty">
          <p className="board-empty__text">
            Доску не удалось нарисовать — ответы в чате не пострадали.
          </p>
        </div>
      }
    >
      <div className="board" id="board-canvas">
        <div className="board__column board__column--main">
          {main.map((node) => (
            <BoardItem
              key={node.id}
              node={node}
              focused={node.id === focused}
              staggerIndex={mainStagger.get(node.id)}
            />
          ))}
        </div>
        {side.length > 0 ? (
          <div className="board__column board__column--side">
            {side.map((node) => (
              <BoardItem
                key={node.id}
                node={node}
                focused={node.id === focused}
                staggerIndex={sideStagger.get(node.id)}
              />
            ))}
          </div>
        ) : null}
      </div>
    </ErrorBoundary>
  );
}
